import { useEffect } from 'react';

import { useBTCProvider, useConnectModal } from '@particle-network/btc-connectkit';
import { base64, hex } from '@scure/base';
import { Transaction } from '@scure/btc-signer';
import axios from 'axios';
import { getAddressInfo } from 'bitcoin-address-validation';
import { Address } from 'sats-connect';
import { create } from 'zustand';

import { BtcLikeWallet, SignPsbtOptions } from '@/networks/btc-like/common/types';

import { convertSignInputsToInputType } from '../../common/transactions';
import { NETWORK } from '../constants';

type PsbtOptions = {
  finalizeSignalInput?: boolean;
  toSignInputs?: {
    index: number;
    address: string;
    sighashTypes?: number[];
    disableTweakSigner?: boolean;
  }[];
};

type CurrentAccountStore = {
  address: string | null;
  publicKey: string | null;
  setAccount: (address: string | null, publicKey: string | null) => void;
};

export const useCurrentAddressStore = create<CurrentAccountStore>((set) => {
  return {
    address: null,
    publicKey: null,
    setAccount: (address, publicKey) => {
      set({ address, publicKey });
    },
  };
});

export const useBtcWallet = (): BtcLikeWallet => {
  const { accounts, getPublicKey, provider } = useBTCProvider();

  const { openConnectModalAsync } = useConnectModal();

  const { address, publicKey, setAccount } = useCurrentAddressStore();

  const { disconnect } = useConnectModal();

  useEffect(() => {
    if (accounts?.length > 0) {
      (async () => {
        const publicKey = await getPublicKey();

        if (address) {
          const type = getAddressInfo(address).type;
          const newAddress = accounts.find((item) => getAddressInfo(item).type === type) ?? accounts[0];

          if ('accounts' in accounts) {
            const currentAccount = (accounts.accounts as Address[]).find((item) => item.address === address);

            if (currentAccount) {
              setAccount(newAddress, currentAccount.publicKey);
              return;
            }
          }

          setAccount(newAddress, publicKey);
        } else {
          setAccount(accounts[0], publicKey);
        }
      })().catch((error) => {
        console.error(error);
      });
    }
  }, [accounts?.join('')]);

  const onConnectHandler = async () => {
    await openConnectModalAsync?.();
  };

  const onDisconnectHandler = () => {
    disconnect?.();
    setAccount(null, null);
  };

  const signPsbt = async (
    psbtBase64: string,
    { broadcast, allowedSignHash, finalizeSignalInput, sighTx }: SignPsbtOptions
  ) => {
    if (!address) {
      throw new Error('Wallet is not connected');
    }

    if (provider.signPsbt) {
      const bytePsbt = base64.decode(psbtBase64);

      let hexPsbt: string;

      if (finalizeSignalInput) {
        const txInitial = Transaction.fromPSBT(bytePsbt, {
          allowUnknownOutputs: true,
          disableScriptCheck: true,
          allowLegacyWitnessUtxo: true,
        });

        txInitial.finalizeIdx(0);

        hexPsbt = hex.encode(txInitial.toPSBT(0));
      } else {
        hexPsbt = hex.encode(bytePsbt);
      }

      const options: PsbtOptions = {
        finalizeSignalInput,
        toSignInputs: convertSignInputsToInputType({ [address]: sighTx }, allowedSignHash, true),
      };

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      const res = await provider.signPsbt(hexPsbt, options);

      const signedBase64Psbt = Buffer.from(res, 'hex').toString('base64');

      if (!broadcast) {
        return {
          signedBase64Psbt,
          txId: null,
        };
      }

      const signedTx = Transaction.fromPSBT(hex.decode(res), {
        allowUnknownOutputs: true,
        disableScriptCheck: true,
      });

      const embedUrl = NETWORK.ExplorerUrl.concat('/api/tx');

      const response = await axios.post<string>(embedUrl, signedTx.hex);

      return {
        signedBase64Psbt,
        txId: response.data,
      };
    } else {
      const options = {
        psbt: psbtBase64,
        allowedSignHash,
        signInputs: { [address]: sighTx },
        broadcast,
      };

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      const sighPsbtResult = await provider.request('signPsbt', options);

      // Apparently error.message can also be an object (eg. with Unisat)
      // check it, pull the string out and throw it
      if (sighPsbtResult.error) {
        const { error } = sighPsbtResult;

        if (typeof error.message === 'object') {
          const { message = 'Error' } = error.message;

          throw new Error(message);
        } else if (typeof error.message === 'string') {
          throw new Error(error.message);
        }
        throw new Error('Error signing PSBT');
      }

      const sighPsbt = sighPsbtResult.result.psbt;

      if (!broadcast) {
        return {
          signedBase64Psbt: sighPsbt,
          txId: null,
        };
      }

      if (provider.isExodus && sighPsbtResult.result.txId) {
        const tx = Transaction.fromPSBT(base64.decode(sighPsbt), {
          allowUnknownOutputs: true,
          disableScriptCheck: true,
        });

        tx.finalize();

        const embedUrl = NETWORK.ExplorerUrl.concat('/api/tx');

        const response = await axios.post<string>(embedUrl, tx.hex);

        return {
          signedBase64Psbt: sighPsbt,
          txId: response.data,
        };
      } else {
        return {
          signedBase64Psbt: sighPsbt,
          txId: sighPsbtResult.result.txid as string,
        };
      }
    }
  };

  const setAddress = (address: string) => {
    if ('accounts' in accounts) {
      const currentAccount = (accounts.accounts as Address[]).find((item) => item.address === address);

      if (currentAccount) {
        setAccount(address, currentAccount.publicKey);
        return;
      }
    }

    if (!publicKey) {
      throw new Error('Public key not found');
    }

    setAccount(address, publicKey);
  };

  return {
    isConnected: !!address && accounts.length > 0,
    address: address,
    publicKey,
    setAddress,
    disconnect: onDisconnectHandler,
    connect: onConnectHandler,
    availableAddresses: accounts,
    signPsbt,
  };
};
